import * as React from 'react';
import {Fragment} from 'react';
import {CoreDI, IEventManagerWithAsync, lazyInject} from '@gongxigames/framework/core';
import {CheatPluginView} from '../../CheatPluginView';
import {CheatReelsSetMatrixController} from './CheatReelsSetMatrixController';
import {CheatReelsSetMatrixEvents} from './CheatReelsSetMatrixEvents';
import {DisplayObjectList} from '../../components/DisplayObjectList';
import {ReelsMatrix} from '../../components/ReelsMatrix';
import {CheatTextInput} from '../../components/CheatTextInput';
import {ButtonWithTitle} from '../../components/ButtonWithTitle';

type CheatReelsSetMatrixViewState = {
    stopPositionsInput: string;
    stopPositionsInputIsInvalid: boolean;
    selectedCell?: {reelIndex: number; rowIndex: number;};
};

export class CheatReelsSetMatrixView extends CheatPluginView<CheatReelsSetMatrixController, {}, CheatReelsSetMatrixViewState> {
    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected eventManager!: IEventManagerWithAsync;

    public state: CheatReelsSetMatrixViewState = {
        stopPositionsInput: '',
        stopPositionsInputIsInvalid: false
    };

    public componentDidMount(): void {
        const {store} = this.controller;

        store.on('loading', () => this.forceUpdate(), this);
        this.eventManager.on(CheatReelsSetMatrixEvents.FORCE_UPDATE, this.onForceUpdate.bind(this), this);
    }

    public componentWillUnmount(): void {
        this.controller.store.off('loading', this);
        this.eventManager.off(CheatReelsSetMatrixEvents.FORCE_UPDATE, this);
    }

    protected onReject(): void {
        this.controller.removeFilterStopPositions();
        this.forceUpdate();
    }

    protected resetStopPositionsInput(): void {
        this.setState({stopPositionsInputIsInvalid: false, stopPositionsInput: ''});
    }

    protected getStopPositionsInput(): number[] {
        const {stopPositionsInput} = this.state;

        if (!stopPositionsInput) {
            return [];
        }

        return stopPositionsInput.split(',').map(pos => isNaN(parseInt(pos, 10)) ? -1 : parseInt(pos, 10));
    }

    protected applyStopPositions(): void {
        const stopPositionInput = this.getStopPositionsInput();
        const stopPositions = stopPositionInput.length ? stopPositionInput : this.controller.reelsSetMatrix.stopPositions;

        this.controller.applyStopFilterPositions(stopPositions);

        if (stopPositionInput.length) {
            this.setState({stopPositionsInput: stopPositions.toString()});
        }
    }

    protected onChange(stopPositionsInput: string): void {
        this.setState({stopPositionsInput});
    }

    protected onClickSymbol(sym: number, reelIndex: number, rowIndex: number): void {
        this.controller.nextSymbolInMatrix(reelIndex, rowIndex);
        this.setState({selectedCell: {reelIndex, rowIndex}});
        this.forceUpdate();
    }

    protected onRightClickSymbol(sym: number, reelIndex: number, rowIndex: number): void {
        this.controller.previousSymbolInMatrix(reelIndex,rowIndex);
        this.setState({selectedCell: {reelIndex, rowIndex}});
        this.forceUpdate();
    };

    protected resetSelectedCell(): void {
        if (this.state.selectedCell) {
            this.controller.resetCell(this.state.selectedCell?.rowIndex, this.state.selectedCell?.reelIndex);
            this.setState({selectedCell: undefined});
            this.forceUpdate();
        }
    }

    public render(): JSX.Element {
        const {stopPositionsInput, stopPositionsInputIsInvalid, selectedCell} = this.state;
        const {matrix, symbolsTextures} = this.controller;
        const textInputWidth = 1500;
        const {loading} = this.controller.store.data;

        if (loading) {
            return <Fragment />;
        }

        return (
            <DisplayObjectList y={80} gapBetweenChildren={100}>
                <ReelsMatrix
                    onClickSymbol={this.onClickSymbol.bind(this)}
                    onRightClickSymbol={this.onRightClickSymbol.bind(this)}
                    matrix={matrix}
                    symbolsTexturesByID={symbolsTextures}
                    selectedCell={selectedCell}
                />
                <DisplayObjectList gapBetweenChildren={20} >
                    <CheatTextInput
                        placeholder={'input field for stop positions'}
                        value={stopPositionsInput}
                        onChange={this.onChange.bind(this)}
                        isInvalid={stopPositionsInputIsInvalid}
                        width={textInputWidth}
                        restrict={/[\d,]*/}
                    />
                    <DisplayObjectList direction={'row'} gapBetweenChildren={20}>
                        <ButtonWithTitle
                            title={'reset input'}
                            fontSize={80}
                            onClick={this.resetStopPositionsInput.bind(this)}
                            height={150} width={textInputWidth / 2}
                        />
                        {selectedCell && <ButtonWithTitle
                            title={'reset selected cell'}
                            fontSize={80}
                            onClick={this.resetSelectedCell.bind(this)}
                            height={150} width={textInputWidth / 2}
                        />}
                    </DisplayObjectList>
                </DisplayObjectList>
                <DisplayObjectList direction={'row'} gapBetweenChildren={20}>
                    <ButtonWithTitle title={'Reject cheat'} fontSize={80} onClick={this.onReject.bind(this)} height={150} width={400} />
                    <ButtonWithTitle title={'Apply cheat'}
                        fontSize={80}
                        onClick={this.applyStopPositions.bind(this)}
                        height={150}
                        width={400}
                    />
                </DisplayObjectList>
            </DisplayObjectList>
        );
    }

    private onForceUpdate(): void {
        this.forceUpdate();
    }
}
